/**
 * Authentication Provider
 * @implements TS-045 - GraphQL Auth Operations and Hooks
 * @implements FS-001 - User Authentication
 * @implements FS-001.5.2 - Background Token Refresh (Session Expiration UI)
 * @implements FS-003.5 - Authentication Integration
 *
 * This provider manages:
 * - User authentication state via useMeQuery
 * - Global UNAUTHENTICATED event handling
 * - Session expiration notification and redirect
 * - Context-based auth distribution to components
 *
 * The provider wraps the application and provides authentication
 * state to all child components via React Context.
 */

import {
  createContext,
  useContext,
  useEffect,
  useCallback,
  useState,
  ReactNode,
} from 'react';
import { useApolloClient } from '@apollo/client';
import { Snackbar, Alert } from '@mui/material';
import { useAuth, UseAuthResult } from '@/hooks/useAuth';

/**
 * Auth context type - exposes the full useAuth interface
 */
type AuthContextType = UseAuthResult;

/**
 * Default context value - used before provider is mounted
 * All functions are no-ops and state indicates loading
 */
/* eslint-disable @typescript-eslint/no-empty-function */
const defaultAuthContext: AuthContextType = {
  user: null,
  isLoading: true,
  isAuthenticated: false,
  error: null,

  loginWithPassword: async () => {},
  isLoggingIn: false,
  loginError: null,

  logout: async () => {},
  isLoggingOut: false,

  registerProductOwner: async () => null,
  isRegistering: false,
  registerError: null,

  loginWithOAuth: () => {},
  registerPOWithOAuth: () => {},

  refetch: async () => {},
};
/* eslint-enable @typescript-eslint/no-empty-function */

/**
 * Auth context
 */
const AuthContext = createContext<AuthContextType>(defaultAuthContext);

/**
 * AuthProvider props
 */
interface AuthProviderProps {
  children: ReactNode;
}

/**
 * Authentication Provider Component
 *
 * Wraps the application and provides authentication state to all components.
 * Uses useMeQuery as the source of truth for auth state.
 *
 * @implements FS-003.7.2 - Error Information (handles UNAUTHENTICATED errors)
 *
 * @example
 * // In App.tsx
 * <ApolloProvider client={apolloClient}>
 *   <AuthProvider>
 *     <App />
 *   </AuthProvider>
 * </ApolloProvider>
 *
 * // In components
 * const { user, isAuthenticated, logout } = useAuthContext();
 */
/**
 * Session expired notification auto-hide duration in milliseconds
 */
const SESSION_EXPIRED_NOTIFICATION_DURATION = 4000;

export const AuthProvider = ({ children }: AuthProviderProps) => {
  const apolloClient = useApolloClient();
  const auth = useAuth();

  /**
   * State for session expiration notification
   * @implements FS-001.5.2 - Session Expiration UI
   */
  const [sessionExpiredNotification, setSessionExpiredNotification] =
    useState(false);

  /**
   * Handle global UNAUTHENTICATED events from Apollo error link
   * @implements FS-003.5.3 - Token Expiry Handling
   * @implements FS-001.5.2 - Session Expiration UI
   *
   * When the backend returns UNAUTHENTICATED error (e.g., token expired),
   * the Apollo error link dispatches an 'auth:unauthorized' event.
   * This handler:
   * 1. Shows a notification to the user
   * 2. Clears local auth state
   * 3. Redirects to login with returnUrl preserved
   */
  const handleUnauthorized = useCallback(async () => {
    console.warn(
      '[AuthProvider] Received unauthorized event, clearing auth state',
    );

    // Show session expired notification
    setSessionExpiredNotification(true);

    // Clear Apollo cache to remove all cached user data
    await apolloClient.clearStore();

    // Refetch to update local state (will return null)
    await auth.refetch();

    // Capture current URL for redirect after login
    // Only preserve if not already on login/register pages
    const currentPath = window.location.pathname + window.location.search;
    const isAuthPage =
      currentPath.startsWith('/login') || currentPath.startsWith('/register');

    if (!isAuthPage) {
      // Redirect to login with returnUrl after showing notification
      // Use setTimeout to allow the notification to be visible briefly
      setTimeout(() => {
        const returnUrl = encodeURIComponent(currentPath);
        window.location.href = `/login?returnUrl=${returnUrl}`;
      }, SESSION_EXPIRED_NOTIFICATION_DURATION);
    }
  }, [apolloClient, auth]);

  /**
   * Handle network errors that might indicate auth issues
   */
  const handleNetworkError = useCallback((event: CustomEvent<Error>) => {
    const error = event.detail;
    console.error('[AuthProvider] Network error:', error.message);

    // Network errors are logged but not automatically handled
    // The UI should display appropriate error states
  }, []);

  /**
   * Set up global event listeners for auth-related events
   * dispatched by the Apollo error link
   */
  useEffect(() => {
    // Listen for unauthorized events from Apollo error link
    const onUnauthorized = () => {
      handleUnauthorized();
    };

    const onNetworkError = (event: Event) => {
      handleNetworkError(event as CustomEvent<Error>);
    };

    window.addEventListener('auth:unauthorized', onUnauthorized);
    window.addEventListener('network:error', onNetworkError);

    return () => {
      window.removeEventListener('auth:unauthorized', onUnauthorized);
      window.removeEventListener('network:error', onNetworkError);
    };
  }, [handleUnauthorized, handleNetworkError]);

  /**
   * Handle closing the session expired notification
   */
  const handleCloseNotification = useCallback(() => {
    setSessionExpiredNotification(false);
  }, []);

  return (
    <AuthContext.Provider value={auth}>
      {children}
      {/* Session Expired Notification
       * @implements FS-001.5.2 - Session Expiration UI
       * Shows non-intrusive notification when session expires
       */}
      <Snackbar
        open={sessionExpiredNotification}
        autoHideDuration={SESSION_EXPIRED_NOTIFICATION_DURATION}
        onClose={handleCloseNotification}
        anchorOrigin={{ vertical: 'top', horizontal: 'center' }}
      >
        <Alert
          onClose={handleCloseNotification}
          severity="info"
          variant="filled"
          sx={{ width: '100%' }}
        >
          Your session has expired. Redirecting to login...
        </Alert>
      </Snackbar>
    </AuthContext.Provider>
  );
};

/**
 * Hook to access authentication context
 *
 * This is the primary way components should access auth state.
 * Must be used within an AuthProvider.
 *
 * @throws Error if used outside of AuthProvider
 *
 * @example
 * const { user, isAuthenticated, login, logout } = useAuthContext();
 *
 * if (!isAuthenticated) {
 *   return <LoginButton onClick={() => login('github')} />;
 * }
 *
 * return (
 *   <div>
 *     Welcome, {user.displayName}!
 *     <LogoutButton onClick={logout} />
 *   </div>
 * );
 */
export const useAuthContext = (): AuthContextType => {
  const context = useContext(AuthContext);

  // Note: We can't easily detect if we're outside the provider
  // because we have a default context value. Components will
  // just see isLoading: true, user: null if provider is missing.
  // This is intentional to avoid runtime errors during SSR or testing.

  return context;
};

/**
 * Legacy alias for useAuthContext
 * @deprecated Use useAuthContext instead for clarity
 */
export { useAuthContext as useAuth };
